import { useMemo, useState } from "react";
import { Alert, View } from "react-native";

import { ProtectedScreen } from "~/components/ui";
import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppLink,
  AppText,
  Button,
  ButtonText,
  Card,
  EmptyState,
  SearchableSelect,
  SegmentedControl,
  Stack,
  TextField,
} from "~/components/ui";
import {
  agingBucket,
  billableValue,
  exportAccountantLedger as saveAccountantLedger,
  exportDateStamp,
  exportHours,
  exportMoney,
  formatHours,
  formatMoney,
  invoiceMatchesRange,
  isOutstanding,
  isOverdue,
  isWithinRange,
  parseReportViewFilters,
  periodRange,
  makeStylesheet,
  type ReportPeriod,
  type ReportViewFilters,
  type TimeState,
} from "~/features/reports";
import { useThemedStyles } from "~/styles/theme";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceInvoices,
  useWorkspaceMembership,
  useWorkspaceProjects,
  useWorkspaceReportViews,
  useWorkspaceTaxSetAsideEntries,
  useWorkspaceTimeEntries,
  useWorkspaceTimeEntryTags,
} from "~/hooks/workspace";
import { useAuth } from "~/hooks/useAuth";
import { useWorkspaceSubscription } from "~/hooks/useSubscription";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import type { Json } from "~/api/tables";

export default function ReportsScreen() {
  const stylesheet = useThemedStyles(makeStylesheet);
  const { user } = useAuth();
  const [period, setPeriod] = useState<ReportPeriod>("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [timeState, setTimeState] = useState<TimeState>("all");
  const [reportViewName, setReportViewName] = useState("");
  const [setAsideLabel, setSetAsideLabel] = useState("");
  const [setAsideNote, setSetAsideNote] = useState("");
  const workspace = usePrimaryWorkspace();
  const subscription = useWorkspaceSubscription(workspace.data?.id);
  const workspaceMembership = useWorkspaceMembership(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const reportViews = useWorkspaceReportViews(workspace.data?.id);
  const setAsideEntries = useWorkspaceTaxSetAsideEntries(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);
  const timeEntryTags = useWorkspaceTimeEntryTags(workspace.data?.id);
  const invoices = useWorkspaceInvoices(workspace.data?.id);
  const entries = timeEntries.data ?? [];
  const invoiceRecords = invoices.data ?? [];
  const clientRecords = clients.data ?? [];
  const projectRecords = projects.data ?? [];
  const clientById = new Map(
    clientRecords.map((client) => [client.id, client]),
  );
  const projectById = new Map(
    projectRecords.map((project) => [project.id, project]),
  );
  const taxPercentage = workspace.data?.tax_set_aside_percentage ?? 0;
  const selectedProject = projectFilter
    ? projectById.get(projectFilter)
    : undefined;
  const clientIdForEntry = (entry: {
    client_id: string | null;
    project_id: string | null;
  }) =>
    entry.client_id ??
    (entry.project_id
      ? (projectById.get(entry.project_id)?.client_id ?? null)
      : null);
  const effectiveInvoiceClientId =
    clientFilter || selectedProject?.client_id || "";
  const filteredClientRecords = clientRecords.filter((client) => {
    if (!effectiveInvoiceClientId) return true;
    return client.id === effectiveInvoiceClientId;
  });
  const range = useMemo(
    () => periodRange(period, customStart, customEnd),
    [customEnd, customStart, period],
  );
  const clientOptions = [
    { label: "All clients", value: "" },
    ...clientRecords.map((client) => ({
      label: client.name,
      value: client.id,
    })),
  ];
  const projectOptions = [
    { label: "All projects", value: "" },
    ...projectRecords
      .filter((project) => !clientFilter || project.client_id === clientFilter)
      .map((project) => ({
        label: project.name,
        value: project.id,
      })),
  ];
  const tagOptions = [
    { label: "All tags", value: "" },
    ...(timeEntryTags.data ?? []).map((tag) => ({
      label: tag.name,
      value: tag.id,
    })),
  ];
  const currentReportFilters: ReportViewFilters = {
    clientFilter,
    customEnd,
    customStart,
    period,
    projectFilter,
    timeState,
  };
  const filteredEntries = entries.filter((entry) => {
    if (!isWithinRange(entry.started_at, range)) return false;
    if (clientFilter && clientIdForEntry(entry) !== clientFilter) return false;
    if (projectFilter && entry.project_id !== projectFilter) return false;
    if (tagFilter && !(entry.tags ?? []).some((tag) => tag.id === tagFilter)) {
      return false;
    }
    if (timeState === "billable" && !entry.billable) return false;
    if (
      timeState === "unbilled" &&
      (!entry.billable || Boolean(entry.invoiced_at))
    ) {
      return false;
    }
    if (timeState === "invoiced" && !entry.invoiced_at) return false;
    return true;
  });
  const filteredInvoices = invoiceRecords.filter((invoice) => {
    if (
      effectiveInvoiceClientId &&
      invoice.client_id !== effectiveInvoiceClientId
    ) {
      return false;
    }
    return invoiceMatchesRange(invoice, range);
  });

  const trackedThisPeriod = filteredEntries.reduce(
    (total, entry) => total + entry.duration_seconds,
    0,
  );
  const billableSeconds = filteredEntries
    .filter((entry) => entry.billable)
    .reduce((total, entry) => total + entry.duration_seconds, 0);
  const unbilledCents = filteredEntries
    .filter((entry) => entry.billable && !entry.invoiced_at)
    .reduce((total, entry) => total + billableValue(entry), 0);
  const paidThisPeriod = filteredInvoices
    .filter(
      (invoice) => invoice.paid_at && isWithinRange(invoice.paid_at, range),
    )
    .reduce((total, invoice) => total + invoice.total_cents, 0);
  const outstandingCents = filteredInvoices
    .filter(isOutstanding)
    .reduce((total, invoice) => total + invoice.total_cents, 0);
  const overdueCents = filteredInvoices
    .filter(isOverdue)
    .reduce((total, invoice) => total + invoice.total_cents, 0);
  const setAsideEstimate = Math.round(
    (paidThisPeriod + unbilledCents) * (taxPercentage / 100),
  );
  const setAsideBasis = paidThisPeriod + unbilledCents;
  const { readOnly } = useWorkspaceReadOnly(workspace.data?.id);
  const canWriteWorkspace =
    !readOnly &&
    Boolean(
      workspace.data?.owner_id === user?.id ||
      workspaceMembership.data?.role === "admin" ||
      workspaceMembership.data?.role === "member" ||
      workspaceMembership.data?.role === "owner",
    );
  const setAsideEntriesInView = setAsideEntries.entries.filter((entry) =>
    isWithinRange(entry.created_at, range),
  );
  const unbilledByClient = filteredEntries
    .filter((entry) => entry.billable && !entry.invoiced_at)
    .reduce<Map<string, number>>((totals, entry) => {
      const clientId = clientIdForEntry(entry);
      if (!clientId) return totals;
      totals.set(clientId, (totals.get(clientId) ?? 0) + billableValue(entry));
      return totals;
    }, new Map());
  const hoursByProject = filteredEntries
    .filter((entry) => entry.project_id)
    .reduce<Map<string, number>>((totals, entry) => {
      const projectId = entry.project_id ?? "";
      totals.set(
        projectId,
        (totals.get(projectId) ?? 0) + entry.duration_seconds,
      );
      return totals;
    }, new Map());
  const topClients = [...unbilledByClient.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 5);
  const topProjects = [...hoursByProject.entries()]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 5);
  const error =
    workspace.error ??
    clients.error ??
    projects.error ??
    reportViews.error ??
    reportViews.create.error ??
    reportViews.update.error ??
    reportViews.remove.error ??
    setAsideEntries.error ??
    setAsideEntries.create.error ??
    setAsideEntries.remove.error ??
    subscription.error ??
    timeEntries.error ??
    invoices.error;
  const errorMessage = error instanceof Error ? error.message : "";

  const metrics = [
    { label: "Paid in view", value: formatMoney(paidThisPeriod) },
    { label: "Unbilled work", value: formatMoney(unbilledCents) },
    { label: "Tracked in view", value: formatHours(trackedThisPeriod) },
    { label: "Set aside estimate", value: formatMoney(setAsideEstimate) },
  ];

  const aging = [
    {
      label: "Due now",
      value: formatMoney(agingBucket(filteredInvoices, 0, 7)),
    },
    {
      label: "8-30 days",
      value: formatMoney(agingBucket(filteredInvoices, 8, 30)),
    },
    {
      label: "31+ days",
      value: formatMoney(agingBucket(filteredInvoices, 31)),
    },
  ];

  function changeClientFilter(value: string) {
    setClientFilter(value);
    if (!projectFilter) return;
    const selected = projectById.get(projectFilter);
    if (selected?.client_id !== value) setProjectFilter("");
  }

  function changeProjectFilter(value: string) {
    setProjectFilter(value);
    const selected = projectById.get(value);
    if (selected?.client_id) setClientFilter(selected.client_id);
  }

  async function exportInvoices() {
    const { saveCsvExport } = await import("~/utils/export-file");
    await saveCsvExport(`mintstint-invoices-${exportDateStamp()}.csv`, [
      [
        "Invoice number",
        "Client",
        "Status",
        "Issue date",
        "Due date",
        "Sent at",
        "Paid at",
        "Subtotal",
        "GST",
        "Total",
        "Notes",
      ],
      ...filteredInvoices.map((invoice) => [
        invoice.invoice_number,
        invoice.client_id
          ? (clientById.get(invoice.client_id)?.name ?? "Unknown client")
          : "",
        invoice.status,
        invoice.issue_date,
        invoice.due_date ?? "",
        invoice.sent_at ?? "",
        invoice.paid_at ?? "",
        exportMoney(invoice.subtotal_cents),
        exportMoney(invoice.gst_cents),
        exportMoney(invoice.total_cents),
        invoice.notes ?? "",
      ]),
    ]);
  }

  async function exportTimeEntries() {
    const { saveCsvExport } = await import("~/utils/export-file");
    await saveCsvExport(`mintstint-time-${exportDateStamp()}.csv`, [
      [
        "Started at",
        "Ended at",
        "Description",
        "Client",
        "Project",
        "Hours",
        "Billable",
        "Hourly rate",
        "Estimated value",
        "Invoiced at",
      ],
      ...filteredEntries.map((entry) => [
        entry.started_at,
        entry.ended_at ?? "",
        entry.description ?? "",
        clientIdForEntry(entry)
          ? (clientById.get(clientIdForEntry(entry) ?? "")?.name ??
            "Unknown client")
          : "",
        entry.project_id
          ? (projectById.get(entry.project_id)?.name ?? "Unknown project")
          : "",
        exportHours(entry.duration_seconds),
        entry.billable ? "Yes" : "No",
        exportMoney(entry.hourly_rate_cents),
        exportMoney(billableValue(entry)),
        entry.invoiced_at ?? "",
      ]),
    ]);
  }

  async function exportClients() {
    const { saveCsvExport } = await import("~/utils/export-file");
    await saveCsvExport(`mintstint-clients-${exportDateStamp()}.csv`, [
      [
        "Name",
        "Contact",
        "Email",
        "Phone",
        "GST number",
        "Billing address",
        "Archived",
        "Notes",
      ],
      ...filteredClientRecords.map((client) => [
        client.name,
        client.contact_name ?? "",
        client.email ?? "",
        client.phone ?? "",
        client.gst_number ?? "",
        client.billing_address ?? "",
        client.archived ? "Yes" : "No",
        client.notes ?? "",
      ]),
    ]);
  }

  async function exportTaxSetAside() {
    const { saveCsvExport } = await import("~/utils/export-file");
    await saveCsvExport(`mintstint-set-aside-${exportDateStamp()}.csv`, [
      ["Metric", "Amount"],
      ["Paid in filtered view", exportMoney(paidThisPeriod)],
      ["Unbilled billable work", exportMoney(unbilledCents)],
      ["Set-aside percentage", String(taxPercentage)],
      ["Estimated set-aside", exportMoney(setAsideEstimate)],
      ["Note", "Planning estimate only, not tax advice or filing output."],
      [],
      [
        "Saved snapshot",
        "Created at",
        "Basis",
        "Percentage",
        "Estimate",
        "Note",
      ],
      ...setAsideEntriesInView.map((entry) => [
        entry.label,
        entry.created_at,
        exportMoney(entry.basis_cents),
        String(entry.percentage),
        exportMoney(entry.estimate_cents),
        entry.note ?? "",
      ]),
    ]);
  }

  async function exportAccountantLedger() {
    if (!workspace.data) return;
    await saveAccountantLedger({
      clientById,
      invoiceRecords,
      range,
      workspace: {
        id: workspace.data.id,
        name: workspace.data.name,
      },
    });
  }

  async function saveSetAsideSnapshot() {
    const label = setAsideLabel.trim();
    if (!label || !canWriteWorkspace) return;
    try {
      await setAsideEntries.create.mutateAsync({
        basisCents: setAsideBasis,
        estimateCents: setAsideEstimate,
        label,
        note: setAsideNote,
        percentage: taxPercentage,
      });
      setSetAsideLabel("");
      setSetAsideNote("");
    } catch {
      // The mutation error is rendered with the report error state.
    }
  }

  function confirmSetAsideRemoval(entry: { id: string; label: string }) {
    Alert.alert(
      `Delete ${entry.label}?`,
      "This removes the saved estimate snapshot without changing invoices or workspace settings.",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: () => setAsideEntries.remove.mutate(entry.id),
        },
      ],
    );
  }

  function applyReportView(filters: ReportViewFilters) {
    const savedProject = filters.projectFilter
      ? projectById.get(filters.projectFilter)
      : undefined;
    const savedClient = filters.clientFilter || savedProject?.client_id || "";
    setPeriod(filters.period);
    setCustomStart(filters.customStart);
    setCustomEnd(filters.customEnd);
    setClientFilter(savedClient);
    setProjectFilter(
      savedProject && (!savedClient || savedProject.client_id === savedClient)
        ? filters.projectFilter
        : "",
    );
    setTimeState(filters.timeState);
  }

  async function saveReportView() {
    const name = reportViewName.trim();
    if (!name) return;
    const existing = reportViews.views.find(
      (view) => view.name.toLowerCase() === name.toLowerCase(),
    );
    const filters = currentReportFilters as unknown as Json;
    if (existing) {
      await reportViews.update.mutateAsync({
        id: existing.id,
        filters,
        name,
      });
    } else {
      await reportViews.create.mutateAsync({ filters, name });
    }
    setReportViewName("");
  }

  return (
    <ProtectedScreen
      title="Reports"
      subtitle="Understand time, income, unbilled work, invoice aging, and estimated set-aside amounts."
    >
      {errorMessage ? (
        <Card danger gap="xs">
          <AppText weight="semibold" tone="danger">
            Reports could not load
          </AppText>
          <AppText size="xs" tone="danger">
            {errorMessage}
          </AppText>
        </Card>
      ) : null}

      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Report filters</AppText>
          <AppText size="xs" tone="muted">
            Filter dashboard totals, breakdowns, and accountant-ready CSV
            exports together.
          </AppText>
        </Stack>
        <View style={stylesheet.filterGrid}>
          <View style={[stylesheet.filterField, stylesheet.wideFilter]}>
            <AppText size="xs" weight="semibold">
              Period
            </AppText>
            <SegmentedControl
              accessibilityLabel="Report period"
              value={period}
              options={[
                { label: "All", value: "all" },
                { label: "Month", value: "month" },
                { label: "Week", value: "week" },
                { label: "Custom", value: "custom" },
              ]}
              onChange={setPeriod}
            />
          </View>
          {period === "custom" ? (
            <>
              <View style={stylesheet.filterField}>
                <AppText size="xs" weight="semibold">
                  Start date
                </AppText>
                <TextField
                  value={customStart}
                  placeholder="2026-07-01"
                  onChangeText={setCustomStart}
                />
              </View>
              <View style={stylesheet.filterField}>
                <AppText size="xs" weight="semibold">
                  End date
                </AppText>
                <TextField
                  value={customEnd}
                  placeholder="2026-07-31"
                  onChangeText={setCustomEnd}
                />
              </View>
            </>
          ) : null}
          <View style={stylesheet.filterField}>
            <AppText size="xs" weight="semibold">
              Client
            </AppText>
            <SearchableSelect
              title="Choose client"
              value={clientFilter}
              options={clientOptions}
              placeholder="All clients"
              onValueChange={changeClientFilter}
            />
          </View>
          <View style={stylesheet.filterField}>
            <AppText size="xs" weight="semibold">
              Project
            </AppText>
            <SearchableSelect
              title="Choose project"
              value={projectFilter}
              options={projectOptions}
              placeholder="All projects"
              onValueChange={changeProjectFilter}
            />
          </View>
          <View style={stylesheet.filterField}>
            <AppText size="xs" weight="semibold">
              Tag
            </AppText>
            <SearchableSelect
              title="Choose tag"
              value={tagFilter}
              options={tagOptions}
              placeholder="All tags"
              onValueChange={setTagFilter}
            />
          </View>
          <View style={[stylesheet.filterField, stylesheet.wideFilter]}>
            <AppText size="xs" weight="semibold">
              Time state
            </AppText>
            <SegmentedControl
              accessibilityLabel="Time entry state"
              value={timeState}
              options={[
                { label: "All", value: "all" },
                { label: "Billable", value: "billable" },
                { label: "Unbilled", value: "unbilled" },
                { label: "Invoiced", value: "invoiced" },
              ]}
              onChange={setTimeState}
            />
          </View>
        </View>
        <View style={stylesheet.actions}>
          <Button
            compact
            variant="outline"
            onPress={() => {
              setPeriod("month");
              setCustomStart("");
              setCustomEnd("");
              setClientFilter("");
              setProjectFilter("");
              setTimeState("all");
            }}
          >
            <ButtonText>Reset filters</ButtonText>
          </Button>
        </View>
      </Card>

      {subscription.canUseAdvancedReports ? (
        <Card gap="md">
          <Stack gap="xs">
            <AppText weight="semibold">Saved report views</AppText>
            <AppText size="xs" tone="muted">
              Save frequently used filter sets for client reviews, project
              retros, billing follow-up, or set-aside planning.
            </AppText>
          </Stack>
          <View style={stylesheet.savedViewForm}>
            <View style={stylesheet.savedViewName}>
              <AppText size="xs" weight="semibold">
                View name
              </AppText>
              <TextField
                accessibilityLabel="View name"
                value={reportViewName}
                placeholder="Monthly unbilled client review"
                onChangeText={setReportViewName}
              />
            </View>
            <Button
              compact
              iconName="save"
              disabled={
                !reportViewName.trim() ||
                reportViews.create.isPending ||
                reportViews.update.isPending
              }
              onPress={() => void saveReportView()}
            >
              <ButtonText>
                {reportViews.create.isPending || reportViews.update.isPending
                  ? "Saving..."
                  : "Save view"}
              </ButtonText>
            </Button>
          </View>
          {reportViews.views.length === 0 ? (
            <EmptyState title="No saved report views yet." />
          ) : (
            <Stack gap="sm">
              {reportViews.views.map((view) => {
                const filters = parseReportViewFilters(view.filters as Json);
                return (
                  <View key={view.id} style={stylesheet.savedViewRow}>
                    <View style={stylesheet.savedViewMain}>
                      <AppText weight="semibold">{view.name}</AppText>
                      <AppText size="xs" tone="muted">
                        {filters
                          ? `${filters.period} - ${filters.timeState}`
                          : "Saved filters need refreshing"}
                      </AppText>
                    </View>
                    <View style={stylesheet.savedViewActions}>
                      <Button
                        compact
                        variant="outline"
                        disabled={!filters}
                        onPress={() => {
                          if (filters) applyReportView(filters);
                        }}
                      >
                        <ButtonText>Apply</ButtonText>
                      </Button>
                      <Button
                        compact
                        variant="danger"
                        disabled={reportViews.remove.isPending}
                        onPress={() =>
                          void reportViews.remove.mutateAsync(view.id)
                        }
                      >
                        <ButtonText>Delete</ButtonText>
                      </Button>
                    </View>
                  </View>
                );
              })}
            </Stack>
          )}
        </Card>
      ) : null}

      <View style={stylesheet.metricsGrid}>
        {metrics.map((metric) => (
          <View key={metric.label} style={stylesheet.metric}>
            <Card gap="xs">
              <AppText size="xs" tone="muted">
                {metric.label}
              </AppText>
              <AppText size="lg" weight="bold">
                {metric.value}
              </AppText>
            </Card>
          </View>
        ))}
      </View>

      <View style={stylesheet.reportGrid}>
        <Card gap="md">
          <Stack gap="xs">
            <AppText weight="semibold">Invoice aging</AppText>
            <AppText size="xs" tone="muted">
              Outstanding invoices grouped by how long they have been overdue.
            </AppText>
          </Stack>
          <Stack gap="sm">
            {aging.map((bucket) => (
              <View key={bucket.label} style={stylesheet.reportRow}>
                <AppText>{bucket.label}</AppText>
                <AppText weight="semibold">{bucket.value}</AppText>
              </View>
            ))}
          </Stack>
        </Card>

        <Card gap="md">
          <Stack gap="xs">
            <AppText weight="semibold">Cash position</AppText>
            <AppText size="xs" tone="muted">
              Current invoice totals before any accounting adjustments.
            </AppText>
          </Stack>
          <Stack gap="sm">
            <View style={stylesheet.reportRow}>
              <AppText>Outstanding</AppText>
              <AppText weight="semibold">
                {formatMoney(outstandingCents)}
              </AppText>
            </View>
            <View style={stylesheet.reportRow}>
              <AppText>Overdue</AppText>
              <AppText weight="semibold">{formatMoney(overdueCents)}</AppText>
            </View>
            <View style={stylesheet.reportRow}>
              <AppText>Invoices</AppText>
              <AppText weight="semibold">
                {String(filteredInvoices.length)}
              </AppText>
            </View>
          </Stack>
        </Card>
      </View>

      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Time mix</AppText>
          <AppText size="xs" tone="muted">
            Billable tracked time compared with all saved entries.
          </AppText>
        </Stack>
        <View style={stylesheet.timeMix}>
          <View style={stylesheet.timeTile}>
            <AppText size="xs" tone="muted">
              Billable
            </AppText>
            <AppText weight="bold">{formatHours(billableSeconds)}</AppText>
          </View>
          <View style={stylesheet.timeTile}>
            <AppText size="xs" tone="muted">
              All tracked
            </AppText>
            <AppText weight="bold">
              {formatHours(
                filteredEntries.reduce(
                  (total, entry) => total + entry.duration_seconds,
                  0,
                ),
              )}
            </AppText>
          </View>
          <View style={stylesheet.timeTile}>
            <AppText size="xs" tone="muted">
              Saved entries
            </AppText>
            <AppText weight="bold">{String(filteredEntries.length)}</AppText>
          </View>
        </View>
      </Card>

      {subscription.canUseConfigurableSetAside ? (
        <Card gap="md">
          <Stack gap="xs">
            <AppText weight="semibold">Set-aside history</AppText>
            <AppText size="xs" tone="muted">
              Save the current filtered estimate as a dated planning snapshot.
              Snapshots do not confirm that money was transferred or replace tax
              advice.
            </AppText>
          </Stack>
          <View style={stylesheet.snapshotSummary}>
            <View style={stylesheet.snapshotMetric}>
              <AppText size="xs" tone="muted">
                Current basis
              </AppText>
              <AppText weight="bold">{formatMoney(setAsideBasis)}</AppText>
            </View>
            <View style={stylesheet.snapshotMetric}>
              <AppText size="xs" tone="muted">
                Current estimate
              </AppText>
              <AppText weight="bold">{formatMoney(setAsideEstimate)}</AppText>
            </View>
            <View style={stylesheet.snapshotMetric}>
              <AppText size="xs" tone="muted">
                Saved in view
              </AppText>
              <AppText weight="bold">
                {String(setAsideEntriesInView.length)}
              </AppText>
            </View>
          </View>
          {canWriteWorkspace ? (
            <View style={stylesheet.snapshotForm}>
              <View style={stylesheet.snapshotLabelField}>
                <AppText size="xs" weight="semibold">
                  Snapshot label
                </AppText>
                <TextField
                  value={setAsideLabel}
                  placeholder="June set-aside review"
                  onChangeText={setSetAsideLabel}
                />
              </View>
              <View style={stylesheet.snapshotNoteField}>
                <AppText size="xs" weight="semibold">
                  Note
                </AppText>
                <TextField
                  value={setAsideNote}
                  placeholder="Optional planning context"
                  onChangeText={setSetAsideNote}
                />
              </View>
              <Button
                compact
                iconName="save"
                disabled={
                  !setAsideLabel.trim() || setAsideEntries.create.isPending
                }
                onPress={() => void saveSetAsideSnapshot()}
              >
                <ButtonText>
                  {setAsideEntries.create.isPending
                    ? "Saving..."
                    : "Save estimate"}
                </ButtonText>
              </Button>
            </View>
          ) : (
            <AppText size="xs" tone="muted">
              Viewers can review saved estimates but cannot change them.
            </AppText>
          )}
          {setAsideEntries.isLoading ? (
            <AppText size="xs" tone="muted">
              Loading saved estimates...
            </AppText>
          ) : setAsideEntriesInView.length === 0 ? (
            <EmptyState title="No saved estimates in this view." />
          ) : (
            <Stack gap="sm">
              {setAsideEntriesInView.slice(0, 12).map((entry) => (
                <View key={entry.id} style={stylesheet.savedViewRow}>
                  <View style={stylesheet.savedViewMain}>
                    <AppText weight="semibold">{entry.label}</AppText>
                    <AppText size="xs" tone="muted">
                      {formatMoney(entry.basis_cents)} at {entry.percentage}% ={" "}
                      {formatMoney(entry.estimate_cents)}
                    </AppText>
                    <AppText size="xs" tone="muted">
                      {new Date(entry.created_at).toLocaleString()}
                      {entry.note ? ` - ${entry.note}` : ""}
                    </AppText>
                  </View>
                  {canWriteWorkspace ? (
                    <Button
                      compact
                      variant="danger"
                      disabled={setAsideEntries.remove.isPending}
                      onPress={() => confirmSetAsideRemoval(entry)}
                    >
                      <ButtonText>Delete</ButtonText>
                    </Button>
                  ) : null}
                </View>
              ))}
            </Stack>
          )}
        </Card>
      ) : (
        <UpgradePrompt reason="Subscribe to keep saved set-aside history and exports active." />
      )}

      {subscription.canUseAdvancedReports ? (
        <>
          <View style={stylesheet.reportGrid}>
            <Card gap="md">
              <Stack gap="xs">
                <AppText weight="semibold">Unbilled by client</AppText>
                <AppText size="xs" tone="muted">
                  Billable uninvoiced time grouped by client.
                </AppText>
              </Stack>
              {topClients.length === 0 ? (
                <AppText size="sm">No unbilled client time yet.</AppText>
              ) : (
                <Stack gap="sm">
                  {topClients.map(([clientId, cents]) => (
                    <View key={clientId} style={stylesheet.reportRow}>
                      <View style={stylesheet.reportMain}>
                        <AppText>
                          {clientById.get(clientId)?.name ?? "Unknown client"}
                        </AppText>
                        <AppLink href={`/clients/${clientId}` as never}>
                          View client
                        </AppLink>
                      </View>
                      <AppText weight="semibold">{formatMoney(cents)}</AppText>
                    </View>
                  ))}
                </Stack>
              )}
            </Card>

            <Card gap="md">
              <Stack gap="xs">
                <AppText weight="semibold">Tracked by project</AppText>
                <AppText size="xs" tone="muted">
                  Saved time grouped by project across the workspace.
                </AppText>
              </Stack>
              {topProjects.length === 0 ? (
                <AppText size="sm">No project-linked time yet.</AppText>
              ) : (
                <Stack gap="sm">
                  {topProjects.map(([projectId, seconds]) => (
                    <View key={projectId} style={stylesheet.reportRow}>
                      <View style={stylesheet.reportMain}>
                        <AppText>
                          {projectById.get(projectId)?.name ??
                            "Unknown project"}
                        </AppText>
                        <AppLink href={`/projects/${projectId}` as never}>
                          View project
                        </AppLink>
                      </View>
                      <AppText weight="semibold">
                        {formatHours(seconds)}
                      </AppText>
                    </View>
                  ))}
                </Stack>
              )}
            </Card>
          </View>

          <Card gap="md">
            <Stack gap="xs">
              <AppText weight="semibold">Exports</AppText>
              <AppText size="xs" tone="muted">
                Download workspace CSVs for invoicing, time review, client
                records, and set-aside planning.
              </AppText>
            </Stack>
            <View style={stylesheet.exportGrid}>
              <Button
                iconName="file-down"
                variant="outline"
                onPress={() => void exportAccountantLedger()}
              >
                <ButtonText>Accountant ledger CSV</ButtonText>
              </Button>
              <Button
                iconName="file-down"
                variant="outline"
                onPress={() => void exportInvoices()}
              >
                <ButtonText>Invoices CSV</ButtonText>
              </Button>
              <Button
                iconName="timer"
                variant="outline"
                onPress={() => void exportTimeEntries()}
              >
                <ButtonText>Time CSV</ButtonText>
              </Button>
              <Button
                iconName="users"
                variant="outline"
                onPress={() => void exportClients()}
              >
                <ButtonText>Clients CSV</ButtonText>
              </Button>
              <Button
                iconName="calculator"
                variant="outline"
                onPress={() => void exportTaxSetAside()}
              >
                <ButtonText>Set-aside CSV</ButtonText>
              </Button>
            </View>
          </Card>
        </>
      ) : (
        <UpgradePrompt reason="Subscribe to keep reports and export workflows active." />
      )}

      <Card gap="sm">
        <AppText weight="semibold">
          {subscription.canUseAdvancedReports
            ? "Advanced reports enabled"
            : "Subscription needed for advanced exports"}
        </AppText>
        <AppText size="xs" tone="muted">
          Set-aside figures are estimates for planning only, not tax advice or
          filing output.
        </AppText>
      </Card>
    </ProtectedScreen>
  );
}
